import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, TextField, Button, Typography, Alert, CircularProgress, Checkbox, FormControlLabel, Divider
} from '@mui/material';
import LockIcon from '@mui/icons-material/LockOutlined';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { mfaVerified, clearMfa } from '../store/authSlice';
import api from '../api/axios';
import AuthLayout from '../components/AuthLayout';

const digitsOnly = (v) => String(v || '').replace(/\D/g, '').slice(0, 6);

const boxSx = (state) => ({
  width: { xs: 44, sm: 48 }, height: 58, boxSizing: 'border-box', borderRadius: '12px',
  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, fontWeight: 600,
  border: '1.5px solid', transition: 'border-color .15s, background-color .25s, color .25s, box-shadow .15s',
  borderColor: { idle: '#D0D5DD', filled: 'primary.main', active: 'primary.main', ok: 'success.main', bad: 'error.main' }[state],
  backgroundColor: { ok: 'success.main', bad: '#FEF3F2' }[state] || 'background.paper',
  color: { ok: '#fff', bad: 'error.main' }[state] || 'text.primary',
  boxShadow: state === 'active' ? '0 0 0 4px #E0E5FF' : 'none'
});

export default function MFAVerify() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { mfaToken: reduxMfaToken } = useSelector((state) => state.auth);
  const mfaToken = location.state?.mfaToken || reduxMfaToken;

  const [code, setCode] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [useBackup, setUseBackup] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);
  const [phase, setPhase] = useState('idle');
  const [error, setError] = useState('');
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    if (!mfaToken) navigate('/login', { replace: true });
  }, [mfaToken, navigate]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const verify = async (value) => {
    if (phase === 'busy' || phase === 'ok') return;
    setError('');
    setPhase('busy');
    try {
      // The server reads only `code` and tells a TOTP from a backup code by its format
      const { data } = await api.post('/api/auth/mfa/verify', { code: value, rememberDevice }, {
        headers: { Authorization: `Bearer ${mfaToken}` }
      });
      setPhase('ok');
      dispatch(mfaVerified({ token: data.token, user: data.user }));
      timer.current = setTimeout(() => navigate('/dashboard', { replace: true }), 700);
    } catch (err) {
      setPhase('bad');
      setError(err.response?.data?.error || "That code didn't match. Try again.");
      setCode('');
      setBackupCode('');
      inputRef.current?.focus();
    }
  };

  const onCode = (raw) => {
    if (phase === 'busy' || phase === 'ok') return;
    const next = digitsOnly(raw);
    setCode(next);
    if (next) { setPhase('idle'); setError(''); }
    if (next.length === 6) verify(next);
  };

  const submit = (e) => {
    e.preventDefault();
    if (useBackup) {
      if (backupCode.trim()) verify(backupCode.trim());
    } else if (code.length === 6) {
      verify(code);
    } else {
      setError('Enter the 6-digit code from your authenticator app.');
      inputRef.current?.focus();
    }
  };

  const switchMode = () => {
    setUseBackup((b) => !b);
    setCode('');
    setBackupCode('');
    setError('');
    setPhase('idle');
  };

  const handleBack = () => {
    dispatch(clearMfa());
    navigate('/login', { replace: true });
  };

  const ok = phase === 'ok';
  const busy = phase === 'busy';
  const boxState = (i) => {
    if (ok) return 'ok';
    if (phase === 'bad') return 'bad';
    if (code[i]) return 'filled';
    if (focused && i === code.length) return 'active';
    return 'idle';
  };

  const badge = (
    <Box
      aria-hidden="true"
      sx={{
        width: 64, height: 64, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'background-color .3s, color .3s',
        backgroundColor: ok ? 'success.main' : phase === 'bad' ? '#FEE4E2' : 'primary.light',
        color: ok ? '#fff' : phase === 'bad' ? 'error.main' : 'primary.main'
      }}
    >
      {ok ? <CheckIcon sx={{ fontSize: 32 }} /> : <LockIcon sx={{ fontSize: 28 }} />}
    </Box>
  );

  return (
    <AuthLayout
      badge={badge}
      title={ok ? "You're verified" : 'Verify it’s you'}
      subtitle={ok
        ? 'Signed in securely. Taking you to your dashboard.'
        : useBackup ? 'Enter one of your backup codes.' : 'Enter the 6-digit code from your authenticator app.'}
    >
      <form onSubmit={submit} noValidate>
        {useBackup ? (
          <TextField
            fullWidth
            label="Backup Code"
            value={backupCode}
            onChange={(e) => { setBackupCode(e.target.value); setError(''); }}
            autoFocus
            autoComplete="off"
            placeholder="xxxxxxxx-xxxx"
            disabled={busy || ok}
          />
        ) : (
          <Box
            className={phase === 'bad' ? 'svx-shake' : undefined}
            sx={{ position: 'relative', display: 'flex', gap: { xs: 1, sm: 1.25 }, justifyContent: 'center' }}
          >
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <Box key={i} aria-hidden="true" sx={boxSx(boxState(i))}>{code[i] || ''}</Box>
            ))}
            <input
              ref={inputRef}
              id="mfa-code"
              aria-label="Authentication code"
              aria-invalid={phase === 'bad'}
              aria-describedby={error ? 'mfa-error' : undefined}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={6}
              autoFocus
              value={code}
              readOnly={busy || ok}
              onChange={(e) => onCode(e.target.value)}
              onPaste={(e) => { e.preventDefault(); onCode(e.clipboardData.getData('text')); }}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, border: 0, fontSize: 16, cursor: 'text' }}
            />
          </Box>
        )}

        {busy && (
          <Typography role="status" variant="body2" color="text.secondary" sx={{ mt: 1.5, textAlign: 'center' }}>
            Verifying…
          </Typography>
        )}

        {error && (
          <Typography
            id="mfa-error"
            role="alert"
            variant="body2"
            sx={{ mt: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, color: 'error.main', fontWeight: 500 }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 18 }} aria-hidden="true" />
            {error}
          </Typography>
        )}

        {!ok && (
          <>
            <FormControlLabel
              control={<Checkbox checked={rememberDevice} onChange={(e) => setRememberDevice(e.target.checked)} />}
              label={<Typography variant="body2">Remember this device for 14 days</Typography>}
              sx={{ mt: 1.5, minHeight: 44 }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={busy || (useBackup ? !backupCode.trim() : code.length !== 6)}
              aria-busy={busy}
              sx={{ mt: 1, minHeight: 48 }}
              startIcon={busy ? <CircularProgress size={18} color="inherit" /> : undefined}
            >
              Verify
            </Button>
          </>
        )}
        {ok && (
          <Alert severity="success" sx={{ mt: 2 }} role="status">Verified. Opening your dashboard…</Alert>
        )}
      </form>

      {!ok && (
        <>
          <Divider sx={{ my: 2 }} />
          <Button fullWidth variant="text" size="small" onClick={switchMode} disabled={busy} sx={{ minHeight: 40 }}>
            {useBackup ? 'Use authenticator app instead' : 'Use a backup code instead'}
          </Button>
          <Button fullWidth variant="text" size="small" onClick={handleBack} disabled={busy} sx={{ mt: 1, minHeight: 40, color: 'text.secondary' }}>
            Back to Login
          </Button>
        </>
      )}
    </AuthLayout>
  );
}
